import AffirmCTA from '@components/AffirmCTA';
import PromotionsAndPricing from '@components/Product/Details/_PromotionsAndPricing';
import Markdown from '@repo/ui/src/components/Markdown';
import CallToActionButton from '@components/Product/CallToActionButton';
import CompatibleProducts from '@components/Product/Details/_CompatibleProducts';
import EstimatedTotal from '@components/Product/Details/_EstimatedTotal';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import { ProductContext } from '@context/ProductProvider';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useProductVariations } from '@hooks/useProductVariations';
import Product from '@models/product';
import { LinkProps } from '@repo/ui/src/types/link';
import { VariationGroup } from '@typings/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React, { useContext } from 'react';
import AlternateSizes from '../AlternateGrillSizes/New';
import AdditionalInformation from './_AdditionalInformation';
import EssentialOrSaleItems from './_EssentialOrSaleItems';
import AlternateColors from './_NewAlternateColors';
import LocallyDropdown from '@components/Product/_LocallyDropdown';

export interface ProductColor {
  color: string;
  link: LinkProps;
  sku: string;
}

export interface AlternateColorProps {
  colors: Array<ProductColor>;
}

interface Props {
  template: PDPTemplate;
  essentials?: Array<{
    id: string;
  }>;
  product: Product;
  dark?: boolean;
  promoMarkdown?: string;
  alternateSize?: Product;
  alternateColors?: AlternateColorProps;
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
  customizable?: boolean;
}

const TierOneProductSelection = ({ product, ownerManual, prop65, dark, customizable }: Props) => {
  const { eCommEnabled, locale } = useCurrentUrl();

  const { t } = useTranslation();
  const {
    isLoading: _isLoadingProductList,
    toggleProduct,
    addOnProductGroups,
    totalPrice: _totalPrice,
    addToCart,
    isLoadingAddToCart,
  } = useContext(ProductContext);

  const [quantity] = React.useState(1);
  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + item.price, 0);
  }, [_totalPrice, product]);

  const [color, size] = useProductVariations(product, [
    VariationGroup.grillColor,
    VariationGroup.grillSize,
  ]);

  const colorsStep = size.variations && size.variations.length > 1 ? 2 : 1;
  const essentialsStep = colorsStep + (color.variations && color.variations.length > 1 ? 1 : 0);
  return (
    <div>
      <div className="tw-grid tw-gap-y-8">
        <ProductHeading
          product={product}
          dark={dark}
          className="md:tw-block tw-hidden"
          hideDescription
        />
        <div className="tw-hidden md:tw-grid tw-gap-y-8">
          <PromotionsAndPricing
            promotions={product?.promotions}
            customizable={customizable}
            product={product}
            dark={dark}
            className="tw-flex"
            isNewTemplate
          />
          {product.shortDescription && (
            <div className="tw-pt-4">
              <Markdown bodySize="xLarge" className={classNames(dark && 'tw-text-white')}>
                {product.shortDescription}
              </Markdown>
            </div>
          )}
        </div>
        {size.variations && size.variations.length > 1 && size.active && (
          <AlternateSizes
            activeSize={size.active}
            sizes={size.variations}
            product={product}
            dark={dark}
          />
        )}
        {color.variations && color.variations.length > 1 && color.active && (
          <AlternateColors
            variationGroup={VariationGroup.grillColor}
            activeColor={color.active}
            colors={color.variations}
            dark={dark}
            step={colorsStep}
          />
        )}
        {addOnProductGroups
          ?.filter((group) => group.addons.length)
          .map((group, i) => (
            <EssentialOrSaleItems
              key={group.label}
              label={group.label}
              addons={group.addons}
              handleSelect={toggleProduct}
              loading={_isLoadingProductList}
              dark={dark}
              step={essentialsStep + i}
            />
          ))}
        {customizable && <EstimatedTotal product={product} dark={dark} />}
        <div className="tw-w-full">
          {eCommEnabled && (
            <div className="tw-text-center">
              {product.ctaButtonType !== 'noCTA' && !!totalPrice && locale === 'en-US' && (
                <AffirmCTA className={dark && 'tw-text-white affirm-dark'} amount={totalPrice} />
              )}
            </div>
          )}
          <div
            className={classNames(
              'tw-flex tw-items-stretch',
              product.ctaButtonType !== 'noCTA' && 'tw-mb-5',
            )}
          >
            {eCommEnabled && (
              <CallToActionButton
                product={product}
                buttonProcessing={isLoadingAddToCart}
                processingText=""
                customProductPrice={totalPrice}
                buttonProps={{
                  disabled: isLoadingAddToCart,
                  full: true,
                  tabIndex: 0,
                  className: isLoadingAddToCart
                    ? 'disabled:tw-bg-action-400'
                    : 'disabled:tw-bg-surface-400',
                }}
                onClickAddToCart={() => addToCart(quantity)}
              />
            )}
          </div>
          {product?.shop?.upc && !product.shop.disableLocally && (
            <LocallyDropdown
              className={classNames(dark && 'locally-dark')}
              upc={product.shop.upc}
            />
          )}
          {customizable && (
            <Markdown className={classNames('tw-py-7', dark && 'tw-text-text-200')}>
              {t('productDetailPageContent.productDisclaimer', undefined, {
                default:
                  '*All items pictured with an accessory are for illustrative purposes only and are not included. The render of the accessory on the grill is only act as an example of suggested use and possible utilization of the item.',
              })}
            </Markdown>
          )}
        </div>
      </div>
      <div className="tw-pt-8 tw-flex tw-gap-5 tw-flex-col">
        {product?.compatibleWith && (
          <CompatibleProducts dark={dark} compatibleWith={product.compatibleWith} />
        )}
        <AdditionalInformation
          dark={dark}
          ownerManual={ownerManual}
          prop65={prop65}
          showGrillAssistanceInfo
        />
      </div>
    </div>
  );
};

export default TierOneProductSelection;
